// Single source for public-api constants. Imported by middleware, utils, jobs.

import { docsUrl } from "@/lib/docs";

/** `doc_url` for every public-api error envelope; all codes share this one reference page. */
export const ERROR_DOC_URL = docsUrl("/docs/reference/errors");

export const PATIENT_SESSION_DEFAULT_EXPIRES_IN = 30 * 60;       // 30 minutes
export const PATIENT_SESSION_MAX_EXPIRES_IN = 24 * 60 * 60;      // 24 hours
export const PATIENT_SESSION_RETURN_URL_SCHEMES = new Set(["https:", "http:"]);

/**
 * Maximum number of webhook endpoints per organization (Q6 in
 * docs/plans/2026-05-15-phase2-webhooks.md). Counted across all statuses;
 * deleting an endpoint frees the slot. Hitting the cap returns 409
 * `conflict_error.webhook_endpoint_limit_exceeded` on the next create.
 */
export const WEBHOOK_ENDPOINT_LIMIT_PER_ORG = 6;

export const API_KEY_LIMIT_PER_ORG = 5;

// Rate limits are defined once in src/server/constants/rate-limits.ts and
// re-exported here so existing importers keep working. Do not redefine them.
export {
  RATE_LIMIT_TIERS,
  RATE_LIMIT_TIERS as RATE_LIMIT_TIER_DEFAULTS,
  RATE_LIMIT_ORG_POOLS,
  RATE_LIMIT_ORG_POOLS as RATE_LIMIT_ORG_POOL_DEFAULTS,
  RATE_LIMIT_POOL_FOR_TIER,
  DEFAULT_RATE_LIMIT_TIER,
  MCP_SESSION_RATE_LIMIT_TIER,
  MCP_ANONYMOUS_RATE_LIMIT_TIER,
  RATE_LIMIT_CACHE_TTL_MS,
  RATE_LIMIT_FALLBACK_TTL_MS,
  MIN_REFILL_PER_SEC,
  isUsableBucketConfig,
} from "@/server/constants/rate-limits";

/** Common error map declared on apiKeyProcedure so every public-api procedure
 *  inherits these in the generated OpenAPI spec. The body shape (error envelope)
 *  is supplied by the openapi generator's customErrorResponseBodySchema option. */
export const API_ERROR_MAP = {
  BAD_REQUEST:           { status: 400, message: "Validation error or unsupported API version" },
  UNAUTHORIZED:          { status: 401, message: "Missing, invalid, or expired API key" },
  FORBIDDEN:             { status: 403, message: "API key lacks the required scope" },
  NOT_FOUND:             { status: 404, message: "Resource not found in this organization" },
  CONFLICT:              { status: 409, message: "Duplicate resource (e.g., external_id already exists for this organization)" },
  TOO_MANY_REQUESTS:     { status: 429, message: "Rate limit exceeded" },
  INTERNAL_SERVER_ERROR: { status: 500, message: "Unexpected server error; safe to retry" },
} as const;
